import { IQueryExecutor, IQuery } from './types/queries';
import axios, { AxiosInstance, CancelTokenSource } from 'axios';
import { handleError } from './errorHandler';
import { bugsnagNotify } from '../utils/bugsnag';
import { redactPublishedAccess } from '../utils/publishedAccessRedaction';
import { DiagnosticRequestConfig, RequestDiagnostics, requestDiagnostics } from './requestDiagnostics';

class QueryExecutor implements IQueryExecutor {
    private client: AxiosInstance;
    private runingQuerise: { token: CancelTokenSource; name: string }[] = [];

    public constructor(baseUrl: string, csrfTokenHeaderKey: string, currentCsrfToken: string | null = null) {
        this.client = axios.create({
            baseURL: baseUrl,
            //timeout: 120000,
        });
        let requestStartedAt: number;
        this.client.interceptors.request.use((x: DiagnosticRequestConfig) => {
            if (x.requestDiagnostics) x.diagnosticStartedAt = new Date().getTime();
            else requestStartedAt = new Date().getTime();
            return x;
        });
        this.client.interceptors.response.use((x) => {
            const config = x.config as DiagnosticRequestConfig;
            const diagnostics = requestDiagnostics(config.requestDiagnostics);
            const responseTime = new Date().getTime() - (diagnostics ? config.diagnosticStartedAt : requestStartedAt);
            if (responseTime > 30000 && diagnostics) {
                bugsnagNotify({ name: 'Long response time', message: `Response time: ${responseTime}ms, operation: ${diagnostics.operation}, stage: ${diagnostics.stage}` });
            } else if (responseTime > 30000) {
                const requestData = JSON.stringify(redactPublishedAccess(x.config));
                bugsnagNotify({ name: 'Long response time', message: `Response time: ${responseTime}ms, request: ${requestData}` });
            }
            return x;
        });

        if (currentCsrfToken != null) {
            this.client.defaults.headers.post[csrfTokenHeaderKey] = currentCsrfToken;
        }
    }

    public executeQuery = async <TResult>(
        query: IQuery<TResult>,
        errorCallback?: (error: any) => { isHandled: boolean; result?: TResult },
        diagnostics?: RequestDiagnostics
    ): Promise<TResult> => {
        const diagnosticContext = requestDiagnostics(diagnostics);
        try {
            return await this.sendRquest(query, diagnosticContext);
        } catch (error) {
            let isHandled = false;
            let result: TResult | undefined;

            if (errorCallback) {
                const handleErrorResult = errorCallback(error);
                isHandled = handleErrorResult.isHandled;
                result = handleErrorResult.result;
            }

            if (!isHandled) {
                if (diagnosticContext) handleError(error, diagnosticContext);
                else handleError(error);
                throw error;
            }

            return result as TResult;
        }
    };

    public abortAllRuningQuery(): void {
        this.runingQuerise.forEach((x) => {
            x.token.cancel('[Abort by force]');
        });
        this.runingQuerise = [];
    }

    private async sendRquest<TResult>(query: IQuery<TResult>, diagnostics?: RequestDiagnostics): Promise<TResult> {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        this.runingQuerise.push({ name: query.QueryType, token: source });

        const res = await this.client.request({
            method: 'post',
            url: `/${query.QueryType}`,
            data: query,
            requestDiagnostics: diagnostics,
            validateStatus: (status: number) => {
                return status == 200; //|| status == 403;
            },
            cancelToken: source.token,
        } as DiagnosticRequestConfig);

        this.runingQuerise = this.runingQuerise.filter((x) => x.token != source);

        if (res.status == 200) {
            return res.data;
        } else {
            console.error('[CQRS] error on api call!', res);
        }
    }
}

const queryExecutor = new QueryExecutor(
    initAppData.appUrls.appUrl + '/api/q',
    initAppData.csrf.csrfTokenHeaderKey,
    initAppData.csrf.currentCsrfToken
);

export { queryExecutor };
